/**
 * @file Menu
 * @desc 左侧菜单 store
 * @author YongZhang<zhangyong06@baidu.com>
 */

'use strict';
import menudata from '../menu';
import {cloneDeep} from 'lodash';

/* eslint-disable */
const authConfig = {
  out: true,
  in: true,
};
/* eslint-enable */

// localStorage key
const pluginListFoldStatusStorageKey = 'pluginListFoldStatus';
// 存储收起/展开状态的对象
const pluginListFoldStatusObject = {};

/**
 * 判断是否本地记录收起/展开状态（目前 家装 不记录收起/展开状态）
 *
 * @return {boolean}
 */
function needLocalStorage() {
    return window._pageData?.industry !== 'decoration';
}

/**
 * 赋值到 store 之前给插件列表中增加属性（增加属性 observer，目前只增加一个 isFold 属性，记录一级插件的收起/展开状态）
 *
 * @param {Array} pluginList 插件列表
 * @return {[]}
 */
function initPluginList(pluginList = []) {
    if (needLocalStorage()) {
        const pluginListFoldStatus = window.localStorage.getItem(
            pluginListFoldStatusStorageKey
        );
        try {
            const pluginListFoldStatusObjectTmp
        = JSON.parse(pluginListFoldStatus) || {};
            Object.assign(pluginListFoldStatusObject, pluginListFoldStatusObjectTmp);
        }
        finally {
            window.localStorage.setItem(
                pluginListFoldStatusStorageKey,
                JSON.stringify(pluginListFoldStatusObject)
            );
        }
    }
    (pluginList || []).forEach(item => {
        if (!item?.list?.length) {
            return;
        }

        item.isFold = !!pluginListFoldStatusObject[item.name];
    });

    return pluginList;
}

/**
 * 根据白名单过滤菜单
 * @param {Array} menuList 原始菜单列表
 * @param {Array} whiteList 白名单路由名称数组
 * @return {Array} 过滤后的菜单列表
 */
function filterMenuByWhiteList(menuList, whiteList) {
    return (menuList || []).filter(item => {
        // 有子菜单的情况递归处理
        if (item.list) {
            item.list = filterMenuByWhiteList(item.list, whiteList);
            // 保留有子菜单的项或自身在白名单中的项
            return item.list.length > 0 || whiteList.includes(item.routerName);
        }
        // 没有子菜单的情况直接检查白名单
        return whiteList.includes(item.routerName);
    });
}

/**
 * 检查用户权限，并根据权限过滤菜单列表
 */
function checkAuth(menuList, userInfo) {
    const authList = (userInfo?.auth || []);
    return filterMenuByWhiteList(menuList, authList);
}

export default {
    namespaced: true,
    state: {
        isLogin: '0',
        userInfoStatus: -1,
        userType: -1,
        menuList: [],
        firstApprove: -1,
        openNames: null, // 当前菜单
        plugins: Object.assign({}, authConfig),
        hasUnReadAnnouncement: false // 是否有未读公告
    },
    mutations: {
    /**
     * 存储登录状态
     *
     * @param {Object} state 状态树
     * @param {string} isLogin 登录状态
     */

        setLoginStatus(state, isLogin) {
            state.isLogin = isLogin;
        },

        setUserInfoStatus(state, userInfoStatus) {
            state.userType = userInfoStatus.userType;
            state.userInfoStatus = userInfoStatus.userInfoStatus;
            state.firstApprove = userInfoStatus.firstApprove;
        },

        setUserType(state, userType) {
            state.userType = userType;
        },

        /**
     * 存储菜单数据
     *
     * @param {Object} state 状态树
     * @param {Object} menuList 菜单list
     */
        setMenuList(state, menuList) {
            if (menuList) {
                state.menuList = initPluginList(menuList);
            }
        },

        // 路由权限控制，递归处理子路由
        isUse(state, params) {
            let plugins = {};
            const useHash = obj => {
                for (let item of obj) {
                    if (!item.routerName && item.list) {
                        useHash(item.list);
                    }
                    else {
                        plugins[item.routerName] = true;
                        params.app && params.app.onInstall(item.routerName);
                    }
                }
                return plugins;
            };
            if (params && params.pluginList && params.pluginList.length > 0) {
                state.plugins = Object.assign(
                    {},
                    authConfig,
                    useHash(params.pluginList)
                );
            }
        },

        // 切换一级菜单的收起/展开状态
        togglePluginFoldStatus(state, params) {
            if (!state.menuList || !state.menuList[params]) {
                return;
            }

            state.menuList[params].isFold = !state.menuList[params].isFold;
            const routerName = state.menuList[params].name;
            pluginListFoldStatusObject[routerName] = state.menuList[params].isFold;
            if (needLocalStorage()) {
                window.localStorage.setItem(
                    pluginListFoldStatusStorageKey,
                    JSON.stringify(pluginListFoldStatusObject)
                );
            }
        },

        // 改变未读公告状态
        setUnReadAnnouncementStatus(state, data) {
            state.hasUnReadAnnouncement = data;
        },
        /**
     * 修改菜单中的 badgeText 值
     * @param {Object} data menu数据
     * @param {String} routeName 路由名称，name 或者 routerName 值
     * @param {String} value 修改后的值
     */
        setBadgeTextValue(state, {routeName, value}) {
            let menuList = cloneDeep(state.menuList);
            for (let item of menuList) {
                if (item.routerName === routeName || item.name === routeName) {
                    item.badgeText = value;
                    state.menuList = menuList;
                    return;
                }
                if (item.list) {
                    for (let subItem of item.list) {
                        if (
                            subItem.routerName === routeName
              || subItem.name === routeName
                        ) {
                            subItem.badgeText = value;
                            state.menuList = menuList;
                            return;
                        }
                    }
                }
            }
        }
    },
    actions: {
    // 更新菜单
        updateMenu({commit, state, dispatch}, params) {
            let bpUserInfo = params.userInfo || {};
            // uc登录用户
            if (+bpUserInfo.uc_shadow_type === 1) {
                commit('setMenuList', checkAuth(menudata.uc, bpUserInfo));
            }
            // 非UC
            else {
                const userRole = bpUserInfo.user_role;
                commit('setMenuList', checkAuth(menudata[userRole], bpUserInfo));
            }
            commit('setLoginStatus', 1);
            // user_role: 0 律师 1 代理商 2 律所 3 代理商-子账号
            commit('setUserInfoStatus', {
                // 律师角色0-专属字段
                userInfoStatus: bpUserInfo?.lawyer_info?.info_status,
                firstApprove: bpUserInfo.first_approve,
                userType: bpUserInfo.user_role
            });
            if (bpUserInfo.merchant_id) {
                window.merchants_id = bpUserInfo.merchant_id;
            }
        },
        // 初始化菜单
        init({commit, state, dispatch}, app) {
            commit('isUse', {
                pluginList: state.menuList,
                app
            });
        },

        // 切换一级菜单的收起/展开状态
        togglePluginFoldStatus({commit, state, dispatch}, params) {
            commit('togglePluginFoldStatus', params);
        }
    }
};
